import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { User, GoalItem } from 'Shared/constants/types';
import { Illustrations } from 'Wpds/components/illustration';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Components
import GoalsCardContainer from 'Shared/components/Goals/GoalsCardContainer/GoalsCardContainer';
import UserProfileEmptyState from './UserProfileEmptyState';
import GoalsTable, { GoalTableColumns } from 'Shared/components/Goals/GoalsTable/GoalsTable';

// Actions
import * as GoalsActions from 'Shared/actions/Goals.actions';

// Selectors
import * as GoalsSelectors from 'Shared/selectors/Goals.selectors';

// Styles
import './UserProfileGoals.scss';

type Props = {
  user: User;
};

const UserProfileGoals = ({ user }: Props) => {
  const history = useHistory();
  const { featureFlagGoalsVersion3: showNewGoals } = useFlags();

  const dispatch = useDispatch();
  const [isLoading, toggleIsLoading] = useToggle(true);

  useEffect(() => {
    const params = {
      [QueryParamConstants.Api.CONTEXT]: QueryParamConstants.Api.ASSIGNED_TO,
      [QueryParamConstants.Api.ASSIGNEE_ID]: user.id,
      [QueryParamConstants.Api.COMPLETED]: false,
    };
    dispatch(GoalsActions.fetchGoals(params)).then(() => {
      toggleIsLoading();
    });
  }, []);

  const goals = useSelector(state =>
    GoalsSelectors.getGoalsByUserIdAndComplete(state, { userId: user.id, complete: false })
  )
    .valueSeq()
    .toJS();

  const handleClick = (goal: GoalItem) => {
    history.push({
      pathname: PageUrlsUtil.makeGoalDetailPageUrl(goal.slug),
      state: { modal: true },
    });
  };

  const GoalsMarkup = () => {
    if (isLoading) {
      return <>loading...</>;
    } else if (goals.length === 0) {
      return (
        <UserProfileEmptyState
          title="There are no goals to see here"
          image={Illustrations.helping_hand_climbing_mountain_purple_bg}
          description={`Any of their goals in workspaces that you share will appear here.`}
        />
      );
    } else {
      if (showNewGoals)
        return (
          <GoalsTable
            goals={goals}
            hiddenColumns={[
              GoalTableColumns.owner,
              GoalTableColumns.workspace,
              GoalTableColumns.department,
              GoalTableColumns.actions,
            ]}
            onRowClick={handleClick}
          />
        );
      return <GoalsCardContainer goals={goals} useFlexLayout />;
    }
  };

  const baseClassName = 'UserProfileGoals';

  return (
    <div className={baseClassName}>
      <h5 className={`${baseClassName}__title`}>Goals</h5>
      <GoalsMarkup />
    </div>
  );
};

export default UserProfileGoals;
